/* eslint-disable @typescript-eslint/naming-convention */
import { useMutation } from '@apollo/client';
import IconArrowRight from '@apollo/icons/default/IconArrowRight.svg';
import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconLink from '@apollo/icons/default/IconLink.svg';
import {
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Select,
  Switch,
} from '@apollo/orbit';
import { IconInfoSolid } from '@apollo/space-kit/icons/IconInfoSolid';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { headersStringFromArray } from 'src/app/graph/explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import { Loading } from 'src/components/common/loading/Loading';
import { HeadersEditor } from 'src/components/headersEditor/HeadersEditor';
import { NewHeaderButton } from 'src/components/headersEditor/NewHeaderButton';
import { useShowToast } from 'src/components/toast/Toast';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { ampli } from 'src/lib/analytics/amplitude/vendor';
import Config from 'src/lib/config';
import { useShowDeployUI } from 'src/lib/featureFlags';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isValidUrl } from 'src/lib/isValidUrl';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import {
  UpdateSendCookiesMutation,
  UpdateSharedHeadersMutation,
  UpdateVariantURLMutation,
} from '../mutations';

import { EndpointInput } from './EndpointInput';

type HeaderEntry = {
  headerName: string;
  value: string;
  key: number;
  checked: boolean;
};

type SubscriptionProtocol =
  | 'auto-detect'
  | 'http-multipart'
  | 'graphql-ws'
  | 'subscriptions-transport-ws';

interface AddEndpointModalProps {
  isOpen: boolean;
  onClose: () => void;
  graphId: string;
  graphName: string;
  variantName: string;
  graphType: GraphQLTypes.GraphType;
  hasManagedSubgraphs?: boolean | null;
}

/**
 * Modal for setting up endpoint configuration for a graph variant.
 * Allows users to enter an endpoint URL directly or get help setting one up.
 */

export const AddEndpointModal = ({
  isOpen,
  onClose,
  graphId,
  graphName,
  variantName,
  graphType,
  hasManagedSubgraphs,
}: AddEndpointModalProps) => {
  const [endpointUrl, setEndpointUrl] = useState('');
  const [isValidEndpointUrl, setIsValidEndpointUrl] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [subscriptionUrl, setSubscriptionUrl] = useState('');
  const [isValidSubscriptionUrl, setIsValidSubscriptionUrl] = useState(true); // Optional field, so valid by default
  const [protocol, setProtocol] = useState<SubscriptionProtocol>('auto-detect');
  const [sendCookies, setSendCookies] = useState(false);
  const [headers, setHeaders] = useState<HeaderEntry[]>([]);
  const history = useHistory();
  const [accountId] = useCurrentAccountId();
  const showDeployUI = useShowDeployUI();
  const { showToasts } = useShowToast();

  // Check if required data is available
  const isDataLoading = !accountId || !graphId || !variantName || !graphName;

  // Two-tier logic:
  // 1. Show QuickDeploy features only for CLASSIC graphs when flag is on
  // 2. Show "Get endpoint" box only when QuickDeploy is shown AND has managed subgraphs
  const showQuickDeploy =
    (showDeployUI && graphType === GraphQLTypes.GraphType.CLASSIC) ||
    graphType === GraphQLTypes.GraphType.SELF_HOSTED_SUPERGRAPH;
  const showGetEndpointBox = showQuickDeploy && hasManagedSubgraphs;

  // Validate subscription URL when it changes
  React.useEffect(() => {
    if (!subscriptionUrl) {
      setIsValidSubscriptionUrl(true); // Empty is valid (optional field)
    } else {
      setIsValidSubscriptionUrl(
        isValidUrl(subscriptionUrl, {
          protocols: ['ws', 'wss', 'http', 'https'],
        }),
      );
    }
  }, [subscriptionUrl]);

  const [updateVariantURL, { loading: isSavingURL }] = useMutation<
    GraphQLTypes.QuickDeploy__UpdateVariantURLMutation,
    GraphQLTypes.QuickDeploy__UpdateVariantURLMutationVariables
  >(UpdateVariantURLMutation);

  const [updateSendCookies, { loading: isSavingCookies }] = useMutation<
    GraphQLTypes.QuickDeploy__UpdateSendCookiesMutation,
    GraphQLTypes.QuickDeploy__UpdateSendCookiesMutationVariables
  >(UpdateSendCookiesMutation);

  const [updateSharedHeaders, { loading: isSavingHeaders }] = useMutation<
    GraphQLTypes.QuickDeploy__UpdateSharedHeadersMutation,
    GraphQLTypes.QuickDeploy__UpdateSharedHeadersMutationVariables
  >(UpdateSharedHeadersMutation);

  const isSaving = isSavingURL || isSavingCookies || isSavingHeaders;

  // Track modal view
  useEffect(() => {
    if (isOpen && graphId && variantName) {
      ampli.updateEndpointModalViewed({
        graph_ref: `${graphId}@${variantName}`,
        modal_version: 'quick_deploy',
      });
    }
  }, [isOpen, graphId, variantName]);

  const handleSave = async () => {
    try {
      // Save URL and subscription URL
      await updateVariantURL({
        variables: {
          graph: graphId,
          variant: variantName,
          url: endpointUrl || null,
          subscriptionUrl: subscriptionUrl || null,
        },
      });

      // Save cookies setting
      await updateSendCookies({
        variables: {
          graph: graphId,
          variant: variantName,
          sendCookies,
        },
      });

      // Save shared headers
      await updateSharedHeaders({
        variables: {
          graph: graphId,
          variant: variantName,
          sharedHeaders:
            headers.length > 0 ? headersStringFromArray(headers) : null,
        },
      });

      // Navigate to graphs page with success modal using overlay pattern
      if (accountId) {
        history.push({
          ...setOverlayVisibleLocation({
            location: {
              ...orgRoutes.graphs.location({ orgId: accountId }),
              state: {
                graphId,
                variantName,
              },
            },
            overlayName: Config.modals.endpointAddedConfirmation,
          }),
        });
      }

      // Track success
      ampli.endpointUpdated({
        graph_ref: `${graphId}@${variantName}`,
        endpoint_changed: true, // Always true since modal only shows when no endpoint exists
        update_source: 'update_endpoint_modal',
      });

      onClose();
    } catch (error) {
      // Track error
      ampli.updateEndpointError({
        graph_ref: `${graphId}@${variantName}`,
        error_name: error instanceof Error ? error.message : 'Unknown error',
        update_source: 'update_endpoint_modal',
      });

      showToasts({
        heading: 'Failed to save endpoint configuration',
        level: 'error',
        dismissable: true,
        message:
          "We couldn't save your endpoint configuration. Please check your connection and try again.",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        {isDataLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loading />
          </div>
        ) : (
          <>
            <ModalHeader>
              <ModalTitle>
                Add endpoint to {graphName}@{variantName}
              </ModalTitle>
              <ModalDescription>
                Your graph variant needs a reachable endpoint so you can get all
                the good GraphOS stuff like live metrics, traces, and insights
                in Studio.
              </ModalDescription>
            </ModalHeader>

            <ModalBody>
              <EndpointInput
                value={endpointUrl}
                onChange={setEndpointUrl}
                placeholder="Enter endpoint URL"
                label="Endpoint URL"
                onValidationChange={setIsValidEndpointUrl}
              />

              {/* Advanced Settings */}
              <div>
                <Button
                  variant="hidden"
                  size="md"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  leftIcon={
                    showAdvanced ? <IconChevronDown /> : <IconChevronRight />
                  }
                  className="mb-2 p-0 text-sm font-medium"
                >
                  Advanced settings
                </Button>

                {showAdvanced && (
                  <div className="space-y-6 rounded-lg border border-primary p-4">
                    {/* Subscriptions */}
                    <FormControl>
                      <FormLabel>Subscriptions</FormLabel>
                      <div className="space-y-4">
                        {/* Subscription Endpoint */}
                        <div>
                          <div className="mb-2 text-sm font-medium text-secondary">
                            Endpoint
                          </div>
                          <Input
                            value={subscriptionUrl}
                            onChange={(e) => setSubscriptionUrl(e.target.value)}
                            placeholder="ws://localhost:4000" // nosemgrep
                            leftElement={<IconLink />}
                          />
                          {subscriptionUrl.startsWith('ws://') && // nosemgrep
                            !subscriptionUrl.includes('localhost') &&
                            !subscriptionUrl.includes('127.0.0.1') && (
                              <div className="mt-2 text-sm text-warning">
                                Most browsers disallow secure websites making
                                requests over insecure protocols such as{' '}
                                {/* nosemgrep */}
                                ws:// - consider using the encrypted wss://
                                instead. {/* nosemgrep */}
                              </div>
                            )}
                        </div>

                        {/* Implementation Protocol */}
                        <div>
                          <div className="mb-2 text-sm font-medium text-secondary">
                            Implementation
                          </div>
                          <Select
                            value={protocol}
                            onChange={(e) =>
                              setProtocol(
                                e.target.value as SubscriptionProtocol,
                              )
                            }
                          >
                            <option value="auto-detect">Auto-detect</option>
                            <option value="graphql-ws">graphql-ws</option>
                            <option value="subscriptions-transport-ws">
                              subscriptions-transport-ws
                            </option>
                            <option value="http-multipart">
                              Multipart subscriptions
                            </option>
                          </Select>
                          <div className="mt-2 text-sm text-secondary">
                            {protocol === 'auto-detect' &&
                              'Automatically determines which websocket protocol to use'}
                            {protocol === 'graphql-ws' &&
                              'Implements the "graphql-transport-ws" protocol'}
                            {protocol === 'subscriptions-transport-ws' &&
                              'Implements the "graphql-ws" protocol'}
                            {protocol === 'http-multipart' &&
                              "Implements Apollo's multipart subscription protocol"}
                          </div>
                        </div>
                      </div>
                    </FormControl>

                    {/* Include Cookies */}
                    <div>
                      <FormControl className="flex flex-row justify-between">
                        <FormLabel>Include cookies</FormLabel>
                        <Switch
                          isChecked={sendCookies}
                          onChange={(e) => setSendCookies(e.target.checked)}
                        >
                          {sendCookies ? 'ON' : 'OFF'}
                        </Switch>
                      </FormControl>

                      <div className="mt-2 text-sm text-secondary">
                        To use cookies in requests:
                        <ol className="mt-2 space-y-2">
                          <li>
                            The cookie's value must contain{' '}
                            <Link
                              to="https://www.chromium.org/updates/same-site"
                              isExternal
                            >
                              SameSite=None; Secure
                            </Link>
                          </li>
                          <li>
                            These CORS headers need to be present in your
                            server's response to Studio:
                            <div className="mt-2 break-words rounded bg-secondary p-2 font-mono text-sm">
                              Access-Control-Allow-Origin:
                              https://studio.apollographql.com
                              <br />
                              Access-Control-Allow-Credentials: true
                            </div>
                          </li>
                        </ol>
                      </div>
                    </div>

                    {/* Shared Headers */}
                    <FormControl>
                      <FormLabel>Shared headers</FormLabel>

                      <HeadersEditor
                        headerDefinitions={headers}
                        setHeaderDefinitions={setHeaders}
                        withCheckbox={false}
                        headersDisabled={false}
                        sharedHeadersDisabled={false}
                      />

                      {!headers.length && (
                        <div className="mb-2 text-sm text-secondary">
                          No shared headers set. Please make sure you{' '}
                          <span className="font-semibold">
                            don't include sensitive data in shared headers
                          </span>
                          ; use environment variables instead, which are not
                          sent to or stored by Apollo.
                        </div>
                      )}

                      <NewHeaderButton
                        className="mt-2 px-0"
                        isSharedHeader={true}
                        onClick={() =>
                          setHeaders(
                            headers.concat({
                              headerName: '',
                              value: '',
                              checked: true,
                              key: Math.random(),
                            }),
                          )
                        }
                      />
                    </FormControl>
                  </div>
                )}
              </div>

              {showGetEndpointBox && (
                <div className="flex items-center justify-between rounded-lg border border-primary bg-secondary p-4">
                  <div className="flex items-center gap-3">
                    <IconInfoSolid className="icon-info size-5" />
                    <span className="text-title">
                      Don't have a public endpoint yet?
                    </span>
                  </div>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => {
                      const deploymentPath = orgRoutes.deploymentFlow.path({
                        orgId: accountId,
                        graphId,
                        variantName,
                      });
                      history.push(deploymentPath);
                      onClose();
                    }}
                    isDisabled={isDataLoading}
                  >
                    Get endpoint
                    <IconArrowRight />
                  </Button>
                </div>
              )}
            </ModalBody>

            <ModalFooter className="border-none">
              <ButtonGroup>
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (isValidEndpointUrl && isValidSubscriptionUrl) {
                      handleSave();
                    }
                  }}
                  isDisabled={
                    isDataLoading ||
                    !isValidEndpointUrl ||
                    !isValidSubscriptionUrl
                  }
                  isLoading={isSaving}
                >
                  Save endpoint
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
